import _ from "lodash";
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { Modal, Tabs } from "@toddle-edu/ds-web";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";

import { useI18n } from "Hooks";
import { useDidUpdate } from "Utils/CustomHooks";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import { compose, graphql } from "lib/apolloClient";
import { getOrganizationId } from "modules/CommonSelectors";
import { recipientModalPaddingStyles } from "Utils/MessageHelpers";
import {
  getMessageReadReceiptQuery,
  getMessageV2ReadReceiptQuery,
} from "./modules/Queries";
import {
  getMessageReadReceiptFromCache,
  getMessageV2ReadReceiptFromCache,
} from "./modules/GraphqlHelpers";

import RecipientsList from "./components/RecipientsList";
import classes from "./ReadReceiptModal.scss";

/**
 * This component will be used once we start showing read receipt in ChatContainer
 */

const SHARED_WITH_KEYS = {
  STAFF: "STAFF",
  STUDENT: "STUDENT",
  FAMILY_MEMBER: "FAMILY_MEMBER",
};

const mapUserTypes = {
  [SHARED_WITH_KEYS.FAMILY_MEMBER]: PARENT,
  [SHARED_WITH_KEYS.STUDENT]: STUDENT,
  [SHARED_WITH_KEYS.STAFF]: STAFF,
};

const userTypeLabels = {
  [STUDENT]: "common:student",
  [PARENT]: "common:family_member",
  [STAFF]: "common:staff_member",
};

const userTypeLabelsPlural = {
  [STUDENT]: "common:student_plural",
  [PARENT]: "common:family_member_plural",
  [STAFF]: "common:staff_members_plural",
};

const DEFAULT_ORDER = [
  SHARED_WITH_KEYS.STUDENT,
  SHARED_WITH_KEYS.STAFF,
  SHARED_WITH_KEYS.FAMILY_MEMBER,
];

const modalStyle = { height: "680px", width: "600px" };

const getRecipientsData = ({ usersRecipients = {} }) => {
  const userReads = _.map(
    _.get(usersRecipients, "user_read.edges", []),
    "node"
  );

  const userUnReads = _.map(
    _.get(usersRecipients, "user_unread.edges", []),
    "node"
  );

  const result = _.reduce(
    DEFAULT_ORDER,
    (acc, type) => {
      acc[mapUserTypes[type]] = {
        readUsers: [],
        unreadUsers: [],
        totalUsersCount: 0,
      };
      return acc;
    },
    {}
  );

  _.forEach(userReads, recipient => {
    const { type } = recipient;

    const isRead = true;

    if (_.isUndefined(result[type])) return;

    result[type].readUsers.push({ isRead, user: recipient });
    result[type].totalUsersCount += 1;
  });

  _.forEach(userUnReads, recipient => {
    const { type } = recipient;

    const isRead = false;

    if (_.isUndefined(result[type])) return;

    result[type].unreadUsers.push({ isRead, user: recipient });
    result[type].totalUsersCount += 1;
  });

  // remove userType with 0 totalCount
  const returnData = _.reduce(
    _.entries(result),
    (acc, [userType, userData]) => {
      if (userData.totalUsersCount > 0) acc[userType] = userData;
      return acc;
    },
    {}
  );

  return returnData;
};

const getLabelMap = totalUsersCount =>
  totalUsersCount === 1 ? userTypeLabels : userTypeLabelsPlural;

const getUserTypeTabs = ({ t, recipientsData }) => {
  return _.map(_.entries(recipientsData), ([userType, data]) => {
    const { totalUsersCount, readUsers } = data;
    const readUsersCount = _.size(readUsers);
    const labelMap = getLabelMap(totalUsersCount);

    const label = t(labelMap[userType]);

    return {
      label,
      value: userType,
      suffix: (
        <div>
          ({readUsersCount}/{totalUsersCount})
        </div>
      ),
    };
  });
};

const modalBodyStyle = {
  padding: "0px",
};

const getInitialUserTypeTab = recipientsData => _.first(_.keys(recipientsData));

const RecipientsModalBody = props => {
  const { recipientsData, t } = props;

  const [currentUserTypeTab, setCurrentUserTypeTab] = useState(() =>
    getInitialUserTypeTab(recipientsData)
  );

  // TODO: can replace with getDerivedStateFromProps
  useDidUpdate(() => {
    if (!_.has(recipientsData, currentUserTypeTab)) {
      setCurrentUserTypeTab(getInitialUserTypeTab(recipientsData));
    }
  }, [recipientsData]);

  const userTypeTabs = getUserTypeTabs({
    t,
    recipientsData,
  });

  const currentUserTypeTabData = recipientsData[currentUserTypeTab];

  const getLabelForSingleTab = () => {
    if (!currentUserTypeTabData) return null;

    const { totalUsersCount, readUsers } = currentUserTypeTabData;
    const readUsersCount = _.size(readUsers);
    const label = _.get(getLabelMap(totalUsersCount), currentUserTypeTab);

    return `${t(label)} (${readUsersCount}/${totalUsersCount})`;
  };

  return (
    <div className={classes.listAndTabContainer}>
      <div className={classes.tabContainer} style={recipientModalPaddingStyles}>
        {_.size(userTypeTabs) > 1 ? (
          <Tabs
            options={userTypeTabs}
            onChange={setCurrentUserTypeTab}
            value={currentUserTypeTab}
          />
        ) : (
          <div className={`heading-6 ${classes.singleTabLabel}`}>
            {getLabelForSingleTab()}
          </div>
        )}
      </div>
      {currentUserTypeTabData ? (
        <RecipientsList
          key={currentUserTypeTab}
          currentTab={currentUserTypeTab}
          data={currentUserTypeTabData}
        />
      ) : null}
    </div>
  );
};

const RecipientsModal = props => {
  const { isLoading, usersRecipients, t } = props;

  const recipientsData = useMemo(() => getRecipientsData({ usersRecipients }), [
    usersRecipients,
  ]);

  if (_.isEmpty(recipientsData)) {
    return isLoading ? (
      <div className={classes.loaderContainer}>
        <Loading />
      </div>
    ) : null;
  }

  return <RecipientsModalBody recipientsData={recipientsData} t={t} />;
};

RecipientsModal.propTypes = {
  data: PropTypes.shape({
    recipientsList: PropTypes.arrayOf(PropTypes.object).isRequired,
    sharedWith: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

const mapStateToProps = state => ({
  organizationId: getOrganizationId(state),
});

const readInput = { messageState: "READ" };
const unreadInput = { messageState: "UNREAD" };
const RecipientsModalWithData = compose(
  connect(mapStateToProps),
  graphql(getMessageReadReceiptQuery, {
    name: "getMessageReadReceipt",
    skip: ({ isMessageV2 }) => isMessageV2,
    options: ({ messageId, organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: messageId, organizationId, readInput, unreadInput },
    }),
    props: ({ getMessageReadReceipt }) => {
      const usersRecipients = getMessageReadReceiptFromCache(
        getMessageReadReceipt.variables
      );

      return {
        isLoading: _.includes([1, 2], getMessageReadReceipt.networkStatus),
        isData: !_.isEmpty(usersRecipients),
        usersRecipients,
      };
    },
  }),
  graphql(getMessageV2ReadReceiptQuery, {
    name: "getMessageV2ReadReceipt",
    skip: ({ isMessageV2 }) => !isMessageV2,
    options: ({ messageId, organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: messageId, organizationId, readInput, unreadInput },
    }),
    props: ({ getMessageV2ReadReceipt }) => {
      const usersRecipients = getMessageV2ReadReceiptFromCache(
        getMessageV2ReadReceipt.variables
      );

      return {
        isLoading: _.includes([1, 2], getMessageV2ReadReceipt.networkStatus),
        isData: !_.isEmpty(usersRecipients),
        usersRecipients,
      };
    },
  }),
  withLoader
)(RecipientsModal);

const RecipientsModalContainer = props => {
  const { message, onCloseModal, organizationId, isMessageV2 = true } = props;

  const { id: messageId } = message;

  const { t } = useI18n();

  return (
    <Modal
      onClose={onCloseModal}
      width={modalStyle.width}
      height={modalStyle.height}
    >
      <Modal.Header hasSeparator={true}>
        {t("common:read_receipts")}
      </Modal.Header>
      <Modal.Body isScrollable style={modalBodyStyle}>
        <RecipientsModalWithData
          messageId={messageId}
          organizationId={organizationId}
          t={t}
          isMessageV2={isMessageV2}
        />
      </Modal.Body>
    </Modal>
  );
};

RecipientsModalContainer.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(RecipientsModalContainer);
